import { Deferred, router } from "@inertiajs/react"
import { AlertCircleIcon, Loader2 } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { CollabHistoryEmpty } from "@/components/crew/collab-history-empty"
import { CrewMemberCollabRow } from "@/components/crew/crew-member-collab-row"
import { CollabTransitionReturnProvider } from "@/components/missions/collab-transition-return"
import { Alert, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { useRetainedDeferredSection } from "@/hooks/use-retained-deferred-section"
import type { CrewMemberCollabHistory } from "@/types/crew"

/** Mirrors `CrewMemberships::CollabWindow::PAGE_SIZE`. */
export const COLLAB_HISTORY_PAGE_SIZE = 20

/**
 * OKS-1038 — « Historique des collaborations » on the crew member page: every
 * collab of this member on the current brand (PRD R1–R9, UX Spec §2–§6).
 *
 * `collab_history` is a DEFERRED prop. ⚠️ OKS-923: `<Deferred>` resets to its
 * fallback on every same-URL visit — including the redirect after a transition
 * and the 302 of a field-error refusal. If the fallback were a different
 * component type React would unmount the rows, and the open dialog (with the
 * brand's typed issue note) with them. So the fallback renders THIS SAME
 * section fed the last resolved payload (`useRetainedDeferredSection`), and the
 * skeleton shows only when nothing has ever resolved for this member.
 */
export function CrewMemberCollabs({
  membershipId,
  history,
}: {
  membershipId: number
  history: CrewMemberCollabHistory | undefined
}) {
  const retained = useRetainedDeferredSection(history, {
    identity: membershipId,
  })
  const loadFailed = useCollabHistoryLoadFailure()

  return (
    <Deferred
      data="collab_history"
      fallback={
        <CrewMemberCollabsSection
          history={retained.section}
          refreshFailed={retained.refreshFailed || loadFailed}
        />
      }
    >
      <CrewMemberCollabsSection history={history} refreshFailed={loadFailed} />
    </Deferred>
  )
}

/**
 * OKS-1038 — UX Spec §3 Error: did the last `collab_history` fetch fail?
 *
 * A failed deferred fetch changes no prop. The first load stays `undefined`,
 * so the section showed its skeleton forever. A failed « Voir plus » kept the
 * old rows and said nothing. The only signal is the router's global events:
 *   - `start` of a visit whose `only` names `collab_history` opens a flight
 *     (and clears a previous failure, so « Réessayer » resets the alert);
 *   - `invalid` (non-Inertia response, e.g. a 500) or `exception` (network)
 *     during that flight marks it failed, and suppresses Inertia's error modal
 *     for THIS visit only — the section's Alert is the error surface;
 *   - `finish` of that visit closes the flight.
 * Failures of any other visit are not this section's.
 */
export function useCollabHistoryLoadFailure(): boolean {
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let inFlight = false
    const concernsList = (only: string[]) => only.includes("collab_history")

    const offStart = router.on("start", (event) => {
      if (!concernsList(event.detail.visit.only)) return
      inFlight = true
      setFailed(false)
    })
    const onFailure = (event: Event) => {
      if (!inFlight) return
      event.preventDefault()
      setFailed(true)
    }
    const offInvalid = router.on("invalid", onFailure)
    const offException = router.on("exception", onFailure)
    const offFinish = router.on("finish", (event) => {
      if (concernsList(event.detail.visit.only)) inFlight = false
    })

    return () => {
      offStart()
      offInvalid()
      offException()
      offFinish()
    }
  }, [])

  return failed
}

export function CrewMemberCollabsSection({
  history,
  refreshFailed,
}: {
  history: CrewMemberCollabHistory | undefined
  refreshFailed: boolean
}) {
  const { t } = useTranslation()
  const [loadingMore, setLoadingMore] = useState(false)
  const firstNewIndex = useRef<number | null>(null)
  const rowNodes = useRef<Map<number, HTMLElement>>(new Map())

  const items = useMemo(() => history?.items ?? [], [history])

  // UX §6 — after « Voir plus », focus the first newly loaded row.
  useEffect(() => {
    const index = firstNewIndex.current
    if (index === null || items.length <= index) return
    firstNewIndex.current = null
    rowNodes.current.get(items[index].id)?.focus()
  }, [items])

  if (history?.total_count === 0) {
    return <CollabHistoryEmpty emptyCopyKey={history.empty_copy_key} />
  }

  const retry = () => router.reload({ only: ["collab_history"] })

  const showMore = () => {
    if (!history) return
    firstNewIndex.current = items.length
    router.reload({
      only: ["collab_history"],
      data: { collabs: history.limit + COLLAB_HISTORY_PAGE_SIZE },
      onStart: () => setLoadingMore(true),
      onFinish: () => setLoadingMore(false),
    })
  }

  const remaining = history ? history.total_count - items.length : 0

  return (
    <section aria-labelledby="crew-member-collabs-title">
      <Card data-testid="creator-detail-collab-history">
        <CardHeader>
          <CardTitle
            id="crew-member-collabs-title"
            className="flex items-center gap-2"
          >
            {t("crew.detail.collab.section_title")}
            {history && (
              <Badge
                variant="secondary"
                data-testid="crew-member-collabs-count"
              >
                {history.total_count}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {refreshFailed && (
            <Alert
              variant="destructive"
              data-testid="crew-member-collabs-error"
            >
              <AlertCircleIcon />
              <AlertTitle className="flex flex-wrap items-center justify-between gap-2">
                {t("crew.detail.collab.load_error")}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="max-md:min-h-11"
                  onClick={retry}
                >
                  {t("crew.detail.collab.retry")}
                </Button>
              </AlertTitle>
            </Alert>
          )}

          {!history && !refreshFailed && (
            <div
              className="flex flex-col gap-4"
              aria-busy="true"
              data-testid="crew-member-collabs-loading"
            >
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex flex-col gap-2">
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="ml-auto h-8 w-40" />
                </div>
              ))}
            </div>
          )}

          {history && (
            <CollabTransitionReturnProvider
              value={{ returnTo: "crew_member", collabsShown: items.length }}
            >
              <div data-testid="crew-member-collabs-list">
                {items.map((row, index) => (
                  <div key={row.id}>
                    {index > 0 && <Separator className="mb-4" />}
                    <CrewMemberCollabRow
                      row={row}
                      articleRef={(node) => {
                        if (node) rowNodes.current.set(row.id, node)
                        else rowNodes.current.delete(row.id)
                      }}
                    />
                  </div>
                ))}
              </div>
            </CollabTransitionReturnProvider>
          )}

          {history && remaining > 0 && (
            <Button
              type="button"
              variant="outline"
              className="w-full max-md:min-h-11"
              onClick={showMore}
              disabled={loadingMore}
              data-testid="crew-member-collabs-show-more"
            >
              {loadingMore && (
                <Loader2
                  aria-hidden="true"
                  className="mr-2 size-4 animate-spin"
                />
              )}
              {t("crew.detail.collab.show_more", {
                count: Math.min(remaining, COLLAB_HISTORY_PAGE_SIZE),
              })}
            </Button>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
